import { AnimatePresence, motion } from 'motion/react';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { Debt } from '../../lib/types';

interface DebtModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddDebt: (debt: Omit<Debt, 'id' | 'uid' | 'currentBalance'> & { initialAmount: number }) => Promise<void>;
}

export function DebtModal({ isOpen, onClose, onAddDebt }: DebtModalProps) {
  const [newDebt, setNewDebt] = useState({ name: '', initialAmount: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDebt.name || !newDebt.initialAmount) return;
    
    await onAddDebt({
      name: newDebt.name,
      initialAmount: parseFloat(newDebt.initialAmount) || 0,
    });
    setNewDebt({ name: '', initialAmount: '' });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-white dark:bg-black border border-zinc-100 dark:border-zinc-900 rounded-[32px] shadow-2xl overflow-hidden transition-colors flex flex-col max-h-[80vh]"
          >
            <div className="p-6 border-b border-zinc-100 dark:border-zinc-900 flex items-center justify-between">
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Add Debt</h2>
              <button onClick={onClose} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end p-4 bg-zinc-50 dark:bg-zinc-950/50 rounded-2xl border border-zinc-100 dark:border-zinc-900">
                <div className="sm:col-span-7 space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Debt Name</label>
                  <input required placeholder="e.g. Credit Card" className="w-full px-4 py-2.5 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white" value={newDebt.name} onChange={e => setNewDebt({...newDebt, name: e.target.value})} />
                </div>
                <div className="sm:col-span-4 space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Initial Amount</label>
                  <input type="number" placeholder="0.00" step="0.01" required className="w-full px-4 py-2.5 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white" value={newDebt.initialAmount} onChange={e => setNewDebt({...newDebt, initialAmount: e.target.value})} />
                </div>
                <button type="submit" className="sm:col-span-1 h-[42px] bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity">
                  <Plus className="w-5 h-5" />
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}